// Shared layout for economy-observability dashboards (epic #550).
// Provides the date-range filter bar (since/until as Unix timestamps),
// optional per-player scope (player picker using GET /api/admin/users?q=),
// loading spinner, error chip, and a titled content area.
//
// The filter bar uses calendar + time pickers with timezone selection.
// External contract: `since`/`until` props and their onChange/onApply
// callbacks still deal in UNIX SECOND STRINGS — the component converts
// datetime + timezone → unix internally via tz.ts (no new dependency).
//
// On first mount, if `since`/`until` are empty the component seeds them
// to [start of current UTC month, now] and fires onApply automatically.
//
// Player picker: when a player is selected `userId` (string of the integer
// user_id) is threaded through via onUserIdChange. Clearing the picker
// resets to all-players (userId = ''). The search reuses the existing
// GET /api/admin/users?q= endpoint.
//
// Usage:
//   <EconomyDashboardLayout
//     title="Casino Metrics"
//     description="…"
//     since={draftSince} until={draftUntil}
//     onSinceChange={setDraftSince} onUntilChange={setDraftUntil}
//     userId={draftUserId} onUserIdChange={setDraftUserId}
//     onApply={handleApply}
//     isLoading={isLoading}
//     isError={isError}
//     errorMessage="Failed to load metrics."
//   >
//     {/* your tables / charts */}
//   </EconomyDashboardLayout>

import React, { useEffect, useRef, useState } from 'react';
import { Button, Field, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { apiFetch } from '@/lib/api';
import {
  wallToUnix,
  unixToWall,
  unixToDisplay,
  currentMonthDefaultRange,
  TIMEZONE_OPTIONS,
  browserTz,
  type TzOption,
} from '@/lib/tz';

export interface EconomyDashboardLayoutProps {
  /** Dashboard title shown in the PageHeader. */
  title: string;
  /** Optional sub-description shown in the PageHeader. */
  description?: string;

  // Date-range filter form (draft values as unix-second strings; caller controls apply timing).
  since: string;
  until: string;
  onSinceChange: (v: string) => void;
  onUntilChange: (v: string) => void;

  // Optional per-player scope. userId is the string of the integer user_id,
  // or '' for all-players. Callers should initialise to ''.
  userId?: string;
  onUserIdChange?: (v: string) => void;

  onApply: () => void;

  isLoading: boolean;
  isError: boolean;
  /** Message displayed in the ErrorChip when isError is true. */
  errorMessage: string;

  children?: React.ReactNode;
}

// ── Timezone select options ────────────────────────────────────────────────────

/** Build the full option list: curated zones + browser local (if not already listed). */
function buildTzOptions(): TzOption[] {
  const local = browserTz();
  const curated = TIMEZONE_OPTIONS;
  const inCurated = curated.some((o) => o.value === local);
  if (inCurated || local === 'UTC') return curated;
  // Prepend browser local after UTC
  return [
    curated[0]!, // UTC
    { value: local, label: `${local} (your local time)` },
    ...curated.slice(1),
  ];
}

// ── Player search types ───────────────────────────────────────────────────────

interface UserSearchResult {
  user_id: number;
  username: string;
  login_name: string;
  email: string;
  active: number;
  locked_at: number | null;
  level: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EconomyDashboardLayout({
  title,
  description,
  since,
  until,
  onSinceChange,
  onUntilChange,
  userId: _userId = '',
  onUserIdChange,
  onApply,
  isLoading,
  isError,
  errorMessage,
  children,
}: EconomyDashboardLayoutProps) {
  const filterId = title.toLowerCase().replace(/\s+/g, '-');
  const tzOptions = useRef(buildTzOptions()).current;

  // Timezone selected in the dropdown (IANA name). Default: UTC.
  const [tz, setTz] = useState<string>('UTC');

  // Wall-clock drafts used by the datetime-local inputs (in the selected tz).
  const [wallSince, setWallSince] = useState<string>('');
  const [wallUntil, setWallUntil] = useState<string>('');

  // Seed current-month default on first mount if since/until are empty.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    if (!since && !until) {
      const [s, u] = currentMonthDefaultRange();
      const sinceStr = String(s);
      const untilStr = String(u);
      onSinceChange(sinceStr);
      onUntilChange(untilStr);
      setWallSince(unixToWall(s, 'UTC'));
      setWallUntil(unixToWall(u, 'UTC'));
      // Fire apply so data loads immediately on first render.
      setTimeout(() => { onApply(); }, 0);
    } else {
      // Props already have values — seed wall-clock inputs from them.
      if (since) setWallSince(unixToWall(Number(since), tz));
      if (until) setWallUntil(unixToWall(Number(until), tz));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only once

  // When tz changes, re-render wall-clock inputs from current unix values.
  const prevTz = useRef(tz);
  useEffect(() => {
    if (prevTz.current === tz) return;
    prevTz.current = tz;
    if (since) setWallSince(unixToWall(Number(since), tz));
    if (until) setWallUntil(unixToWall(Number(until), tz));
  }, [tz, since, until]);

  // Handle wall-clock input changes: convert → unix and push to caller.
  function handleWallSinceChange(val: string) {
    setWallSince(val);
    if (val) {
      const unix = wallToUnix(val, tz);
      if (!isNaN(unix)) onSinceChange(String(unix));
    }
  }

  function handleWallUntilChange(val: string) {
    setWallUntil(val);
    if (val) {
      const unix = wallToUnix(val, tz);
      if (!isNaN(unix)) onUntilChange(String(unix));
    }
  }

  // Dual-display: format the current unix values in both UTC and chosen tz.
  const sinceNum = since ? Number(since) : null;
  const untilNum = until ? Number(until) : null;

  function renderDisplay() {
    if (!sinceNum && !untilNum) return null;

    const fmt = (unix: number | null, zone: string) =>
      unix != null ? unixToDisplay(unix, zone) : '—';

    const showUtc = tz !== 'UTC';

    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="tz-dual-display">
        {showUtc && (
          <div>
            <span className="font-medium">UTC: </span>
            {fmt(sinceNum, 'UTC')} – {fmt(untilNum, 'UTC')}
          </div>
        )}
        <div>
          <span className="font-medium">{tz}: </span>
          {fmt(sinceNum, tz)} – {fmt(untilNum, tz)}
        </div>
      </div>
    );
  }

  // ── Player picker ────────────────────────────────────────────────────────────

  const [playerQuery, setPlayerQuery] = useState<string>('');
  const [playerResults, setPlayerResults] = useState<UserSearchResult[]>([]);
  const [playerSearching, setPlayerSearching] = useState<boolean>(false);
  const [selectedPlayer, setSelectedPlayer] = useState<UserSearchResult | null>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derive whether the picker is available (caller passed the handler).
  const hasPicker = typeof onUserIdChange === 'function';

  function handlePlayerQueryChange(val: string) {
    setPlayerQuery(val);
    if (!val.trim()) {
      setPlayerResults([]);
      return;
    }
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      setPlayerSearching(true);
      try {
        const res = await apiFetch<{ ok: boolean; users: UserSearchResult[] }>(
          `/admin/users?q=${encodeURIComponent(val.trim())}`,
        );
        if (res.ok) setPlayerResults(res.data.users);
      } catch {
        // silently ignore search errors
      } finally {
        setPlayerSearching(false);
      }
    }, 300);
  }

  function handleSelectPlayer(player: UserSearchResult) {
    setSelectedPlayer(player);
    setPlayerQuery('');
    setPlayerResults([]);
    onUserIdChange!(String(player.user_id));
  }

  function handleClearPlayer() {
    setSelectedPlayer(null);
    setPlayerQuery('');
    setPlayerResults([]);
    onUserIdChange!('');
  }

  function renderPlayerPicker() {
    if (!hasPicker) return null;
    return (
      <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid="economy-player-picker">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-ink-mid">Scope to player:</span>
          {selectedPlayer ? (
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-mono bg-muted border border-border rounded px-2 py-0.5"
                data-testid="economy-player-selected"
              >
                {selectedPlayer.username} [{selectedPlayer.user_id}]
              </span>
              <button
                type="button"
                onClick={handleClearPlayer}
                className="text-xs text-red-600 hover:underline"
                data-testid="economy-player-clear"
                aria-label="Clear player filter"
              >
                Clear
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={playerQuery}
                onChange={(e) => handlePlayerQueryChange(e.target.value)}
                placeholder="Search username or user ID…"
                className="h-8 w-56 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                aria-label="Search player"
                data-testid="economy-player-search"
              />
              {playerSearching && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-low">…</span>
              )}
              {playerResults.length > 0 && (
                <ul
                  className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background shadow-md"
                  data-testid="economy-player-results"
                >
                  {playerResults.map((u) => (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        onClick={() => handleSelectPlayer(u)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                        data-testid={`economy-player-result-${u.user_id}`}
                      >
                        <span className="font-mono">{u.username}</span>
                        <span className="ml-2 text-xs text-ink-low">[{u.user_id}]</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {selectedPlayer && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
              Filtered to {selectedPlayer.username}
            </span>
          )}
          {!selectedPlayer && (
            <span className="text-xs text-ink-low">All players (default)</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />

      {/* Date-range filter bar */}
      <div
        className="flex flex-col gap-4 rounded-sm border border-border bg-muted p-4"
        data-testid="economy-date-filter"
      >
        {/* Row 1: pickers */}
        <div className="flex flex-wrap gap-3 items-end">
          <Field label="Start">
            <input
              id={`${filterId}-since`}
              type="datetime-local"
              value={wallSince}
              onChange={(e) => handleWallSinceChange(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </Field>
          <Field label="End">
            <input
              id={`${filterId}-until`}
              type="datetime-local"
              value={wallUntil}
              onChange={(e) => handleWallUntilChange(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </Field>
          <Field label="Timezone">
            <select
              id={`${filterId}-tz`}
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="Timezone"
            >
              {tzOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Button type="button" onClick={onApply} disabled={isLoading}>
            Apply
          </Button>
        </div>

        {/* Row 2: dual-display (UTC + chosen zone) */}
        {renderDisplay()}

        {/* Row 3: player picker (when caller opts in via onUserIdChange) */}
        {renderPlayerPicker()}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && <ErrorChip>{errorMessage}</ErrorChip>}

      {/* Content (only rendered when not loading and not errored) */}
      {!isLoading && !isError && children}
    </div>
  );
}
